import { en } from '@/shared/i18n/locales/en';
import { es } from '@/shared/i18n/locales/es';
import { fr } from '@/shared/i18n/locales/fr';
import { it } from '@/shared/i18n/locales/it';
import { nl } from '@/shared/i18n/locales/nl';
import { zh } from '@/shared/i18n/locales/zh';
import { getScheduleOverride } from '@/shared/i18n/schedule-locale-overrides';
import type { Locale, TranslationSchema } from '@/shared/i18n/types';

export const translations: Record<Locale, TranslationSchema> = {
  en,
  it,
  es,
  fr,
  nl,
  zh,
};

type NestedKeyOf<T, Prefix extends string = ''> = T extends object
  ? {
      [K in keyof T & string]: T[K] extends object
        ? NestedKeyOf<T[K], `${Prefix}${K}.`>
        : `${Prefix}${K}`;
    }[keyof T & string]
  : never;

export type TranslationKey = NestedKeyOf<typeof en>;

function getNestedValue(
  obj: TranslationSchema,
  path: string,
): string | undefined {
  const keys = path.split('.');
  let current: unknown = obj;

  for (const key of keys) {
    if (
      current === null ||
      current === undefined ||
      typeof current !== 'object'
    ) {
      return undefined;
    }
    current = (current as Record<string, unknown>)[key];
  }

  return typeof current === 'string' ? current : undefined;
}

export function translate(
  locale: Locale,
  key: TranslationKey,
  params?: Record<string, string>,
  fallback?: string,
): string {
  const dictionary = translations[locale];
  let text =
    getScheduleOverride(locale, key) ??
    getNestedValue(dictionary, key) ??
    getNestedValue(en, key) ??
    fallback ??
    key;

  if (params) {
    for (const [paramKey, value] of Object.entries(params)) {
      text = text.replaceAll(`{{${paramKey}}}`, value);
    }
  }

  return text;
}

export * from '@/shared/i18n/types';
